// Drawn product pictures for stores without photos (the v4 board's tiles): a
// garment outline picked from the style's name or category, filled with its
// colour on a light wash of the same colour, with a fabric texture where the
// name says what it is made of. Styles we cannot place return null and keep
// the initials placeholder rather than a drawing of the wrong thing.

// 64×80 viewBox, drawn 6px down so the floor shadow sits under the hem.
export const GARMENT_PATHS = {
  shirt: "M24 8 14 12 6 28 14 32 18 26 18 58 46 58 46 26 50 32 58 28 50 12 40 8 32 17Z",
  tee: "M23 8 9 15 12 27 18 25 18 58 46 58 46 25 52 27 55 15 41 8C38 13 26 13 23 8Z",
  kurta: "M24 5 14 9 8 31 15 33 18 22 16 61 48 61 46 22 49 33 56 31 50 9 40 5 32 14Z",
  trouser: "M18 6H46L50 60H36L32 22 28 60H14Z",
  shorts: "M16 16H48L52 46H36L32 30 28 46H12Z",
  dress: "M26 5H38L40 17 36 23 50 60H14L28 23 24 17Z",
  top: "M24 8H40L41 16 50 22 47 50H17L14 22 23 16Z",
  shoe: "M8 50C8 41 12 35 19 35H27C31 35 34 39 38 41L51 45C56 46 58 49 58 53V57H8Z",
  // Long sleeves and a deep V where the lapels meet.
  jacket: "M23 6 12 10 6 50 14 52 17 26 17 62 47 62 47 26 50 52 58 50 52 10 41 6 32 30Z",
  // Long sleeves, ribbed hem and a hood rising behind the neck.
  hoodie: "M22 10 10 16 7 46 15 47 18 28 18 60 46 60 46 28 49 47 57 46 54 16 42 10C42 1 22 1 22 10Z",
  skirt: "M23 12H41L54 60H10Z",
  // A length of cloth hung from its top edge with a fringed end: saree, dupatta, stole.
  drape: "M14 8H50L54 58 49 54 44 58 39 54 34 58 29 54 24 58 19 54 14 58 10 58Z",
};

// Checked in order: the first rule whose words appear wins, so "T-Shirt" is a
// tee before the shirt rule sees it, "Denim Shorts" is shorts, not jeans, and
// "Denim Jacket" is a jacket before the trouser rule reads "denim".
const SHAPE_RULES = [
  ["shoe", /\b(shoes?|sneakers?|loafers?|sandals?|boots?|heels|flats|slippers?|footwear|mojaris?|juttis?)\b/],
  ["hoodie", /\b(hoodies?|hooded)\b/],
  ["jacket", /\b(jackets?|blazers?|coats?|overcoats?|waistcoats?|bundis?|nehru|bombers?|parkas?|shrugs?|cardigans?)\b/],
  ["tee", /\b(t-?shirts?|tees?|polos?|tank|vest|sweatshirts?)\b/],
  ["shorts", /\b(shorts|bermudas?|boxers?|trunks)\b/],
  ["trouser", /\b(jeans|trousers?|chinos?|pants|joggers?|leggings|palazzos?|denims?|bottoms?|salwars?|pyjamas?|churidars?|dhotis?)\b/],
  ["kurta", /\b(kurtas?|kurtis?|anarkalis?|tunics?|sherwanis?)\b/],
  ["skirt", /\b(skirts?|lehengas?|ghagras?)\b/],
  ["dress", /\b(dress(es)?|frocks?|gowns?|maxi|midi)\b/],
  ["drape", /\b(sarees?|saris?|dupattas?|stoles?|shawls?|odhnis?)\b/],
  ["top", /\b(tops?|blouses?|camis?|crop)\b/],
  ["shirt", /\b(shirts?|overshirts?)\b/],
];

const FABRIC_RULES = [
  ["denim", /\b(jeans|denims?)\b/],
  ["linen", /\blinen\b/],
  ["oxford", /\boxford\b/],
  ["twill", /\b(twill|chinos?|flannel|checked|check)\b/],
  ["pique", /\b(polos?|piqu[eé])\b/],
  ["crepe", /\b(crepe|georgette|chiffon|rayon|viscose)\b/],
  ["chikan", /\b(chikan(kari)?|lucknowi)\b/],
  ["jersey", /\b(t-?shirts?|tees?|jersey|knit)\b/],
];

const norm = (s) => String(s || "").toLowerCase();

// The name is more specific than the category ("Women's Dresses & Tops" holds both),
// so it is tried first.
export function garmentFor(name, categoryName) {
  const n = norm(name);
  const c = norm(categoryName);
  let shape = null;
  for (const text of [n, c]) {
    const hit = SHAPE_RULES.find(([, re]) => re.test(text));
    if (hit) {
      shape = hit[0];
      break;
    }
  }
  if (!shape) return null;
  const fabric = shape === "shoe" ? null : FABRIC_RULES.find(([, re]) => re.test(n))?.[0] || null;
  return { shape, fabric };
}

const FABRIC_LABEL = { denim: "Denim", linen: "Linen", oxford: "Oxford", twill: "Twill", pique: "Piqué", crepe: "Crepe", chikan: "Chikankari", jersey: "Jersey" };

const GENDER_RULES = [
  ["Kids", /\b(kids?|boys?|girls?|infants?|toddlers?|baby)\b/],
  ["Women", /\b(women'?s?|ladies|female)\b/],
  ["Men", /\b(men'?s?|gents|male)\b/],
  ["Unisex", /\bunisex\b/],
];

// "Denim · Men" for a tile with no photo: the fabric the name implies and the
// department from the category (else the name). "" when neither is known.
export function garmentMeta(name, categoryName) {
  const fabric = FABRIC_LABEL[garmentFor(name, categoryName)?.fabric] || "";
  const gender = [norm(categoryName), norm(name)].map((t) => GENDER_RULES.find(([, re]) => re.test(t))?.[0]).find(Boolean) || "";
  return [fabric, gender].filter(Boolean).join(" · ");
}

// Ground for the drawing: the colour mixed 80% toward white, so a navy shirt
// sits on a pale navy wash and white garments still read on their tile.
export function garmentGround(hex) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const six = m[1].length === 3 ? m[1].replace(/./g, "$&$&") : m[1];
  const n = parseInt(six, 16);
  const mix = (c) => Math.round(c + (255 - c) * 0.8);
  return `rgb(${mix(n >> 16)},${mix((n >> 8) & 255)},${mix(n & 255)})`;
}
